// PayoutSetupForm.tsx
import React, { useState } from 'react';
import { Animated, Text, TextInput, View, Pressable, ActivityIndicator } from 'react-native';
import { AntDesign } from '@expo/vector-icons';

interface PayoutRulesSelectProps {
  isLoadingOnboard: boolean;
  handleSave: (e: React.TouchEvent | any) => void;
  setPayoutCadence: (cadence: 'monthly' | 'weekly') => void;
  setPayoutDate: (date: number) => void;
  setPayoutDay: (day: number) => void;
  payoutCadence: string;
  payoutDate: number;
  payoutDay: number;
  isPayoutCadenceOpen: boolean;
  setPayoutCadenceOpen: (isOpen: boolean) => void;
  isPayoutDayOpen: boolean;
  setPayoutDayOpen: (isOpen: boolean) => void;
}

const PayoutRulesSelect: React.FC<PayoutRulesSelectProps> = ({
  isLoadingOnboard,
  handleSave,
  setPayoutCadence,
  setPayoutDate,
  setPayoutDay,
  payoutCadence,
  payoutDate,
  payoutDay,
  isPayoutCadenceOpen,
  setPayoutCadenceOpen,
  isPayoutDayOpen,
  setPayoutDayOpen
}) => {
  // Object structure for Weekly and Monthly cadence options
  const cadenceOptions: Array<{ displayText: string; dbText: 'monthly' | 'weekly' }> = [
    { displayText: 'Weekly', dbText: 'weekly' },
    { displayText: 'Monthly', dbText: 'monthly' },
  ];

  // Object structure for day of the week options
  const dayOfWeekOptions = [
    { displayText: 'Sunday', dbValue: 0 },
    { displayText: 'Monday', dbValue: 1 },
    { displayText: 'Tuesday', dbValue: 2 },
    { displayText: 'Wednesday', dbValue: 3 },
    { displayText: 'Thursday', dbValue: 4 },
    { displayText: 'Friday', dbValue: 5 },
    { displayText: 'Saturday', dbValue: 6 },
  ];

  const isPayoutDateInvalid = isNaN(Number(payoutDate)) || 
                                (payoutCadence === 'monthly' && Number(payoutDate) < 1) ||
                                (payoutCadence === 'weekly' && Number(payoutDate) < 0) ||
                                Number(payoutDate) > 31;

  return (
    <View>
        <Text style={{ color: '#40BFB4', marginLeft: 34, marginRight: 16, marginBottom: 10 }}>
          Set your payout cadence
        </Text>

        {/* Payout Cadence Dropdown */}
        <Pressable onPress={() => setPayoutCadenceOpen(!isPayoutCadenceOpen)}>
          <View
            style={{
              backgroundColor: "#292D33",
              borderRadius: 45,
              zIndex: 9999,
              marginBottom: 15,
              padding: 10,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 4,
              elevation: 5,
              position: 'relative', // Ensure that the arrow can be positioned correctly
              flexDirection: 'row', // Ensure text and icon are aligned horizontally
              justifyContent: 'space-between', // Ensure space between text and icon
              alignItems: 'center', // Center text and icon vertically
            }}
          >
            <Text style={{ color: "#fff", paddingVertical: 10, paddingHorizontal: 15 }}>
              {payoutCadence ? cadenceOptions.find(option => option.dbText === payoutCadence)?.displayText : "Select Payout Cadence"}
            </Text>
            <AntDesign
                    name={isPayoutCadenceOpen ? 'upcircleo' : 'downcircleo'}
                    size={20}
                    color="#fff"
                    style={{ marginRight: 15}}
            />
          </View>
        </Pressable>

        {isPayoutCadenceOpen && (
          <View style={{ backgroundColor: "#292D33", borderRadius: 8, marginBottom: 15 }}>
            {cadenceOptions.map((item, index) => {
              const isSelected = item.dbText === payoutCadence;
              return (
                <View key={item.dbText}>
                  <Pressable
                    onPress={() => {
                      setPayoutCadence(item.dbText);
                      setPayoutCadenceOpen(false); // Close the dropdown after selection
                    }}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 15 }}>
                      <Text style={{ fontWeight: isSelected ? 'bold' : 'normal', color: "#fff" }}>{item.displayText}</Text>
                      {isSelected && <AntDesign name="check" size={16} color="white" style={{ backgroundColor: '#39BEB7', borderRadius: 999, padding: 2 }} />}
                    </View>
                  </Pressable>
                  {index < cadenceOptions.length - 1 && <View style={{ height: 1, backgroundColor: "#ccc" }} />}
                </View>
              );
            })}
          </View>
        )}

        {/* Payout Date Input or Weekday Dropdown */}
        {payoutCadence === 'monthly' && (
          <>
            <Text style={{ color: '#40BFB4', marginLeft: 34, marginRight: 16, marginBottom: 10 }}>
              Set the day of the month for your payout
            </Text>
            <TextInput
              style={{
                height: 40,
                borderColor: '#ccc',
                borderWidth: 1,
                borderRadius: 8,
                paddingHorizontal: 10,
                marginBottom: 5,
                color: '#fff',
              }}
              keyboardType="numeric"
              value={String(payoutDate)}
              onChangeText={(text: string) => setPayoutDate(Number(text))}
              maxLength={2}
              placeholder="Enter day (1-31)"
            />
            {isPayoutDateInvalid && (
              <Text style={{ color: 'red', marginLeft: 34, marginBottom: 5 }}>Please enter a valid number between 1 and 31.</Text>
            )}
          </>
        )}

        {payoutCadence === 'weekly' && (
          <>
            <Text style={{ color: '#40BFB4', marginLeft: 34, marginRight: 16, marginBottom: 10 }}>
              Set your payout day
            </Text>
            <Pressable onPress={() => setPayoutDayOpen(!isPayoutDayOpen)}>
              <View
                style={{
                  backgroundColor: "#292D33",
                  borderRadius: 45,
                  zIndex: 9999,
                  marginBottom: 15,
                  padding: 10,
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.25,
                  shadowRadius: 4,
                  elevation: 5,
                  position: 'relative',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text style={{ color: "#fff", paddingVertical: 10, paddingHorizontal: 15, fontWeight: 'bold' }}>
                    {payoutDay !== undefined && payoutDay !== null
                        ? dayOfWeekOptions.find(option => option.dbValue === payoutDay)?.displayText 
                        : "Select Payout Day"}
                </Text>
                {/* Down Arrow for Dropdown */}
                <AntDesign
                        name={isPayoutDayOpen ? 'upcircleo' : 'downcircleo'}
                        size={20}
                        color="#fff"
                        style={{ marginRight: 15}}
                />
              </View>
            </Pressable>

            {isPayoutDayOpen && (
              <View style={{ backgroundColor: "#292D33", borderRadius: 8, marginBottom: 15 }}>
                {dayOfWeekOptions.map((item, index) => {
                  const isSelected = item.dbValue === payoutDay;
                  return (
                    <View key={item.dbValue}>
                      <Pressable
                        onPress={() => {
                          setPayoutDay(item.dbValue);
                          setPayoutDayOpen(false); // Close the dropdown after selection
                        }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 15 }}>
                          <Text style={{ fontWeight: isSelected ? 'bold' : 'normal', color: "#fff" }}>{item.displayText}</Text>
                          {isSelected && <AntDesign name="check" size={16} color="white" style={{ backgroundColor: '#39BEB7', borderRadius: 999, padding: 2 }} />}
                        </View>
                      </Pressable>
                      {index < dayOfWeekOptions.length - 1 && <View style={{ height: 1, backgroundColor: "#ccc" }} />}
                    </View>
                  );
                })}
              </View>
            )}
          </>
        )}

        {/* Save Button */}
        <Pressable
          onPress={handleSave}
          disabled={isLoadingOnboard || isPayoutDateInvalid} // Disable the button if it's loading or payoutDate is invalid
          style={({ pressed }) => [
            {
              backgroundColor: isLoadingOnboard || isPayoutDateInvalid ? 'gray' : '#39BEB7',
              marginTop: 10,
              marginHorizontal: 34,
              paddingVertical: 12,
              borderRadius: 45,
              alignItems: 'center',
              opacity: pressed ? 0.8 : 1,
            },
          ]}
        >
          {isLoadingOnboard ? (
            <ActivityIndicator />
          ) : (
            <Text style={{ color: 'white', fontWeight: '600' }}>
              Save
            </Text>
          )}
        </Pressable>
      </View>
    );
};

export default PayoutRulesSelect;
